import { chatApis } from '@/configs/apis/chat';
import { contactsApis } from '@/configs/apis/contacts';
import { userApis } from '@/configs/apis/user';
import type { ChatGroupMember } from '@/contexts/chat/scripts/hooks/useChatGroup/types';
import { ChatCategory } from '../useChats/types';
import { genSearchMember } from '../useChatGroup/utils';

type Member = ChatGroupMember;

interface Props {
  value: string;
  type: 'contacts' | 'global' | 'chat';
  excludeMembers?: Member[];
}

export default function useSearchMembers<T extends Member>({
  value,
  type,
  excludeMembers = [],
}: Props) {
  const [list, setList] = useState<T[]>([]);

  const deferredValue = useDeferredValue(value);

  const filterList = useMemo(() => {
    if (!excludeMembers.length) return list;
    const excludeMemberIds = new Set(excludeMembers.map((item) => item.user_id));
    return list.filter((item) => !excludeMemberIds.has(item.user_id));
  }, [list, excludeMembers]);

  const { run: onSearch } = useDebounceFn(
    async () => {
      let res: T[] = [];
      switch (type) {
        case 'contacts': {
          const result = await contactsApis.queryContactsByName({
            name: deferredValue,
            limit: -1,
            offset: 0,
          });
          res = result.map((item: any) => genSearchMember(item));
          break;
        }
        case 'chat': {
          const result = await chatApis.querySessionByName({
            name: deferredValue,
            category: ChatCategory.SINGLE,
            limit: -1,
            offset: 0,
          });
          res = result.map((item: any) => genSearchMember(item));
          break;
        }
        case 'global': {
          const result = await userApis.search(deferredValue);
          res = result.map((item: any) => genSearchMember(item));
          break;
        }
        default:
          break;
      }
      setList(res);
    },
    { wait: 300 }
  );

  useEffect(() => {
    onSearch();
  }, [deferredValue, onSearch]);

  return {
    list: filterList,
  };
}
